import { supabase } from '../config/supabase';
import logger from './logger';

export type TelemetryLevel = 'info' | 'warn' | 'error';

export interface TelemetryEvent {
  id?: string;
  event_name: string;
  event_category: string;
  level: TelemetryLevel;
  session_id?: string | null;
  actor_wallet?: string | null;
  subject_wallet?: string | null;
  attestation_uid?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
}

const TELEMETRY_TABLE = 'telemetry_events';
const TELEMETRY_SESSION_KEY = 'buan-telemetry-session-id';
const TELEMETRY_LOCAL_FALLBACK_KEY = 'buan-telemetry-fallback-events';
const TELEMETRY_LOCAL_FALLBACK_LIMIT = 2000;

const sortTelemetryByNewest = (events: TelemetryEvent[]): TelemetryEvent[] => {
  return [...events].sort((left, right) => {
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return rightTime - leftTime;
  });
};

const readLocalFallbackEvents = (): TelemetryEvent[] => {
  try {
    const raw = window.localStorage.getItem(TELEMETRY_LOCAL_FALLBACK_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? sortTelemetryByNewest(parsed as TelemetryEvent[]) : [];
  } catch {
    return [];
  }
};

const writeLocalFallbackEvents = (events: TelemetryEvent[]): void => {
  try {
    window.localStorage.setItem(
      TELEMETRY_LOCAL_FALLBACK_KEY,
      JSON.stringify(sortTelemetryByNewest(events).slice(0, TELEMETRY_LOCAL_FALLBACK_LIMIT))
    );
  } catch {
    // Ignore local cache write failures.
  }
};

const cacheFailedTelemetryEvent = (event: TelemetryEvent, reason: string): void => {
  const normalized: TelemetryEvent = {
    ...event,
    id: event.id || `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    created_at: event.created_at || new Date().toISOString(),
    metadata: {
      ...(event.metadata || {}),
      _fallbackStoredLocally: true,
      _fallbackReason: reason,
    },
  };

  const existing = readLocalFallbackEvents();
  writeLocalFallbackEvents([normalized, ...existing]);
};

const makeSessionId = (): string => {
  try {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    logger.warn('Could not use crypto.randomUUID for telemetry session:', error);
  }

  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getTelemetrySessionId = (): string => {
  try {
    const existing = window.localStorage.getItem(TELEMETRY_SESSION_KEY);
    if (existing) {
      return existing;
    }
    const created = makeSessionId();
    window.localStorage.setItem(TELEMETRY_SESSION_KEY, created);
    return created;
  } catch {
    return makeSessionId();
  }
};

export const createTelemetryThrottle = (windowMs: number) => {
  const lastSent = new Map<string, number>();

  return (key: string): boolean => {
    const now = Date.now();
    const previous = lastSent.get(key) || 0;

    if (now - previous < windowMs) {
      return false;
    }

    lastSent.set(key, now);
    return true;
  };
};

export class Telemetry {
  static async track(event: TelemetryEvent): Promise<boolean> {
    try {
      const record = {
        ...event,
        session_id: event.session_id || getTelemetrySessionId(),
        created_at: event.created_at || new Date().toISOString(),
        actor_wallet: event.actor_wallet?.toLowerCase() || null,
        subject_wallet: event.subject_wallet?.toLowerCase() || null,
        attestation_uid: event.attestation_uid || null,
        metadata: event.metadata || null,
      };

      const { error } = await supabase.from(TELEMETRY_TABLE).insert([record]);

      if (error) {
        logger.warn('Telemetry insert failed:', error.message);
        cacheFailedTelemetryEvent(record, error.message || 'insert_failed');
        return false;
      }

      return true;
    } catch (error) {
      logger.warn('Telemetry insert failed with exception:', error);
      cacheFailedTelemetryEvent(event, error instanceof Error ? error.message : 'exception');
      return false;
    }
  }

  static async getRecent(limit = 50): Promise<TelemetryEvent[]> {
    try {
      const { data, error } = await supabase
        .from(TELEMETRY_TABLE)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.warn('Telemetry query failed:', error.message);
        return [];
      }

      return sortTelemetryByNewest((data || []) as TelemetryEvent[]).slice(0, limit);
    } catch (error) {
      logger.warn('Telemetry query failed with exception:', error);
      return [];
    }
  }

  static async getSince(hours: number, limit = 1000): Promise<TelemetryEvent[]> {
    try {
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from(TELEMETRY_TABLE)
        .select('*')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.warn('Telemetry since query failed:', error.message);
        return [];
      }

      return sortTelemetryByNewest((data || []) as TelemetryEvent[]).slice(0, limit);
    } catch (error) {
      logger.warn('Telemetry since query failed with exception:', error);
      return [];
    }
  }

  static async getSinceByCategory(
    hours: number,
    eventCategory: string,
    limit = 1000
  ): Promise<TelemetryEvent[]> {
    try {
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from(TELEMETRY_TABLE)
        .select('*')
        .eq('event_category', eventCategory)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.warn('Telemetry since-by-category query failed:', error.message);
        return [];
      }

      return sortTelemetryByNewest((data || []) as TelemetryEvent[]).slice(0, limit);
    } catch (error) {
      logger.warn('Telemetry since-by-category query failed with exception:', error);
      return [];
    }
  }

  static async getByCategory(eventCategory: string, limit = 5000): Promise<TelemetryEvent[]> {
    try {
      const { data, error } = await supabase
        .from(TELEMETRY_TABLE)
        .select('*')
        .eq('event_category', eventCategory)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.warn('Telemetry by-category query failed:', error.message);
        return [];
      }

      return sortTelemetryByNewest((data || []) as TelemetryEvent[]).slice(0, limit);
    } catch (error) {
      logger.warn('Telemetry by-category query failed with exception:', error);
      return [];
    }
  }

  static async getLocalRecent(limit = 200): Promise<TelemetryEvent[]> {
    return sortTelemetryByNewest(readLocalFallbackEvents()).slice(0, limit);
  }

  static async getLocalSince(hours: number, limit = 2000): Promise<TelemetryEvent[]> {
    const cutoff = Date.now() - hours * 60 * 60 * 1000;
    return sortTelemetryByNewest(
      readLocalFallbackEvents().filter((event) => {
        if (!event.created_at) {
          return false;
        }
        return new Date(event.created_at).getTime() >= cutoff;
      })
    ).slice(0, limit);
  }

  static async getLocalByCategory(eventCategory: string, limit = 5000): Promise<TelemetryEvent[]> {
    return sortTelemetryByNewest(
      readLocalFallbackEvents().filter((event) => event.event_category === eventCategory)
    ).slice(0, limit);
  }
}
